'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Send, Plus, Search, Loader2, RefreshCw, Inbox, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { useSellerId } from '@/hooks/useSellerId'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { fetchConversations, fetchMessages, type Conversation, type Message } from '@/lib/inbox/inboxClient'
import { useInboxSocket } from '@/hooks/useInboxSocket'
import { LinkifiedText } from '@/components/common/LinkifiedText'

type CommunityMember = {
  customer_id: number
  name: string
  email?: string
  avatar_url?: string | null
  screenname?: string | null
}

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function initialsFrom(text?: string) {
  const t = (text || '').trim()
  if (!t) return 'DM'
  const parts = t.split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase()).join('') || 'DM'
}

function formatMessageTime(ts?: string | null) {
  if (!ts) return ''

  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()

  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (isSameDay) {
    // Hoy → solo hora
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)

  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()

  if (isYesterday) {
    return `Ayer, ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`
  }

  // Antes → "Oct 25, 09:30 AM"
  return date.toLocaleDateString([], {
    month: 'short',
    day: '2-digit',
  }) + ', ' +
    date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
}

function moveConversationToTop(convs: Conversation[], conversationId: number) {
  const idx = convs.findIndex(c => c.conversation_id === conversationId)
  if (idx <= 0) return convs
  const copy = convs.slice()
  const [item] = copy.splice(idx, 1)
  copy.unshift(item)
  return copy
}

export function InboxChat({
  sellerId,
  initialConversationId,
  mode = 'full',
}: {
  sellerId?: number
  initialConversationId?: number
  mode?: 'full' | 'thread'
}) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const threadOnly = mode === 'thread'

  const { userId, isLoading } = useCurrentUser() // ajusta al shape real de tu hook

  const {
    connected,
    socket,
    isOnline,
  } = useInboxSocket(userId)

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const [messages, setMessages] = useState<Message[]>([])
  const [text, setText] = useState('')

  const [loadingConvs, setLoadingConvs] = useState(true)
  const [loadingMsgs, setLoadingMsgs] = useState(false)
  const { sellerId: sellerIdFromHook, loading: sellerLoading } = useSellerId()

  const [resolvedSellerId, setResolvedSellerId] = useState<number | null>(null)
  const effectiveSellerId = sellerId ?? resolvedSellerId ?? undefined

  // typing indicator
    const [peerTyping, setPeerTyping] = useState(false)
    const typingIdleTimer = useRef<any>(null)

    // de-dupe de mensajes (clientId o messageId)
    const seenClientIdsRef = useRef<Set<string>>(new Set())
    const seenMessageIdsRef = useRef<Set<number>>(new Set())

  // Modal Nuevo Mensaje
  const [openNew, setOpenNew] = useState(false)
  const [q, setQ] = useState('')
  const qd = useDebouncedValue(q, 250)
  const [people, setPeople] = useState<CommunityMember[]>([])
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [startingId, setStartingId] = useState<number | null>(null)
  const [errorPeople, setErrorPeople] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Scroll inteligente
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const scrollViewportRef = useRef<HTMLDivElement | null>(null)
  const shouldAutoScrollRef = useRef(true)

  // leer ?c= del URL (si existe)
  const urlConversationId = useMemo(() => {
    const c = searchParams.get('c')
    return c ? Number(c) : null
  }, [searchParams])

  const effectiveInitialConversationId = initialConversationId ?? urlConversationId ?? null

  function emitTyping(isTyping: boolean) {
    if (!selectedId) return
    // tu hook debe exponer socket.typing(conversationId, isTyping)
    socket.typing?.(selectedId, isTyping)
  }

  function onTextChange(v: string) {
    setText(v)
    if (!selectedId) return

    emitTyping(true)

    if (typingIdleTimer.current) clearTimeout(typingIdleTimer.current)
    typingIdleTimer.current = setTimeout(() => {
        emitTyping(false)
    }, 900)
  }

  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(false)
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < breakpoint)
        onResize()
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [breakpoint])
    return isMobile
  }

  const isMobile = useIsMobile()
  const showThread = isMobile ? !!selectedId : true


  async function reloadConversations(pickId?: number) {
    try {
      setLoadingConvs(true)
      const convs = await fetchConversations(userId)
      setConversations(convs)

      const preferred = pickId ?? selectedId ?? effectiveInitialConversationId
      if (preferred && convs.some(c => c.conversation_id === preferred)) {
        setSelectedId(preferred)
        return
      }

      if (!preferred && convs.length) setSelectedId(convs[0].conversation_id)
    } catch {
      // opcional: toast
    } finally {
      setLoadingConvs(false)
    }
  }

  // Load conversations (first)
  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!mounted) return
      await reloadConversations(effectiveInitialConversationId ?? undefined)
    })()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveInitialConversationId])

  // Si el URL trae ?c=, selecciónalo (sin pelear con estado)
  useEffect(() => {
    if (!effectiveInitialConversationId) return
    setSelectedId(prev => prev ?? effectiveInitialConversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveInitialConversationId])

  // Load messages when conversation changes
  useEffect(() => {
    if (!selectedId) return

    let mounted = true
    ;(async () => {
      try {
        setLoadingMsgs(true)
        const msgs = await fetchMessages(userId, selectedId)
        if (!mounted) return
        setMessages(msgs)

        const last = msgs[msgs.length - 1]
        if (last?.message_id) socket.read(selectedId, last.message_id)

        // al entrar a un thread, auto-scroll al final
        shouldAutoScrollRef.current = true
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }))
      } catch {
        // opcional: toast
      } finally {
        if (mounted) setLoadingMsgs(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [selectedId, socket, userId])

  // Realtime incoming messages
  useEffect(() => {
    const offMsg = socket.onMessage?.((m) => {
        // de-dupe por clientId o messageId
        if (m.clientId) {
        if (seenClientIdsRef.current.has(m.clientId)) return
        seenClientIdsRef.current.add(m.clientId)
        }
        if (m.messageId) {
        if (seenMessageIdsRef.current.has(m.messageId)) return
        seenMessageIdsRef.current.add(m.messageId)
        }

        // preview + timestamp + mover arriba
        setConversations((prev) => {
        const updated = prev.map((c) =>
            c.conversation_id === m.conversationId
            ? {
                ...c,
                last_body: m.body,
                last_sender_id: m.senderId,
                last_message_at: m.dateAdded,
                }
            : c
        )
        return moveConversationToTop(updated, m.conversationId)
        })

        // append si es la conversación actual
        if (m.conversationId === selectedId) {
        setMessages((prev) => [
            ...prev,
            {
            message_id: m.messageId ?? Date.now(),
            conversation_id: m.conversationId,
            sender_id: m.senderId,
            body: m.body,
            date_added: m.dateAdded,
            },
        ])

        // si llegó del peer, apaga typing
        if (m.senderId !== userId) setPeerTyping(false)

        // read receipt automático (si existe)
        if (m.messageId) socket.read?.(m.conversationId, m.messageId)
        }
    })

    const offTyping = socket.onTyping?.((t) => {
        // { conversationId, userId, isTyping }
        if (t.conversationId !== selectedId) return
        if (t.userId === userId) return
        setPeerTyping(!!t.isTyping)
    })

    return () => {
        offMsg?.()
        offTyping?.()
    }
    }, [selectedId, socket, userId])

  useEffect(() => {
    setPeerTyping(false)
    if (typingIdleTimer.current) clearTimeout(typingIdleTimer.current)
    // no limpiamos seen sets globales para evitar duplicados al navegar
  }, [selectedId])

  useEffect(() => {
    const off = socket.onRead(({ conversationId, userId: readerId, messageId }) => {
        // solo si es de la conversación actual
        setConversations(prev =>
        prev.map(c =>
            c.conversation_id === conversationId
            ? {
                ...c,
                peer_last_read_message_id:
                    c.peer?.id === readerId
                    ? Math.max(c.peer_last_read_message_id || 0, messageId)
                    : c.peer_last_read_message_id,
                }
            : c
        )
        )
    })
    return () => off?.()
  }, [socket])


  // Scroll: solo si el usuario está cerca del fondo
  useEffect(() => {
    if (!shouldAutoScrollRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  function onScrollCapture() {
    const el = scrollViewportRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    shouldAutoScrollRef.current = distanceFromBottom < 120
  }

  const selectedConv = useMemo(
    () => conversations.find((c) => c.conversation_id === selectedId) || null,
    [conversations, selectedId]
  )

    // ─────────────────────────────
    // READ RECEIPTS (Visto)
    // ─────────────────────────────
    const peerRead = selectedConv?.peer_last_read_message_id || 0

    const lastMineId = useMemo(() => {
    let id = 0
    for (const m of messages) {
        if (m.sender_id === userId) id = m.message_id
    }
    return id
    }, [messages, userId])

    const isLastMineSeen = peerRead >= lastMineId && lastMineId > 0


  function selectConversation(id: number) {
    setSelectedId(id)

    if (!threadOnly) {
      router.replace(`/inbox?c=${id}`)
    }
  }

  async function send() {
    const body = text.trim()
    if (!body || !selectedId) return

    emitTyping(false)

    const clientId = crypto.randomUUID()
    setText('')

    // optimistic
    const optimisticId = Date.now()
    const nowIso = new Date().toISOString()

    if (!userId) return // o throw

    setMessages((prev) => [
        ...prev,
        {
        message_id: optimisticId,
        conversation_id: selectedId,
        sender_id: userId,
        body,
        date_added: nowIso,
        },
    ])

    try {
        const res = await fetch('/api/inbox/messages', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            'x-user-id': String(userId),
        },
        body: JSON.stringify({
            conversationId: selectedId,
            body,
            clientId,
        }),
        })

        const json = await res.json()
        if (!res.ok || !json?.ok) throw new Error(json?.error || 'SEND_FAILED')

        const saved = json.message as Message

        // replace optimistic with real
        setMessages((prev) =>
        prev.map((m) => (m.message_id === optimisticId ? saved : m))
        )

        // sidebar preview + move to top
        setConversations((prev) => {
        const updated = prev.map((c) =>
            c.conversation_id === selectedId
            ? {
                ...c,
                last_body: saved.body,
                last_sender_id: saved.sender_id,
                last_message_at: saved.date_added,
                }
            : c
        )
        return moveConversationToTop(updated, selectedId)
        })

        // prevent local duplicate if your listener uses clientId dedupe
        // (only if you have this ref in scope)
        seenClientIdsRef.current.add(clientId)

        // realtime broadcast (DB already persisted ✅)
        socket.emitMessage({
          conversationId: selectedId,
          messageId: saved.message_id,
          senderId: userId,
          body: saved.body,
          dateAdded: saved.date_added,
          clientId,
        })
    } catch (e) {
        console.error('Send failed', e)
        // TODO: mark message failed / retry UI if you want
    }
    }

    

  // ─────────────────────────────
  // Modal: buscar miembros comunidad
  // ─────────────────────────────

  useEffect(() => {
    if (!openNew) return
    if (sellerId) return
    if (sellerLoading) return
    if (!sellerIdFromHook) return

    setResolvedSellerId(sellerIdFromHook)
  }, [openNew, sellerId, sellerLoading, sellerIdFromHook])

  useEffect(() => {
    if (!openNew) return
    const t = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [openNew])

  useEffect(() => {
    if (!openNew) return
    if (!sellerId) return

    let cancelled = false

    ;(async () => {
      try {
        setLoadingPeople(true)
        setErrorPeople(null)
        if (!effectiveSellerId) return

        const url = `/api/community/members?q=${encodeURIComponent(qd)}&sellerId=${encodeURIComponent(
          String(sellerId)
        )}&page=1&limit=25`

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'x-user-id': String(userId),
          },
        })

        const json = await res.json()
        if (!res.ok || !json?.ok) throw new Error(json?.error || 'SEARCH_FAILED')

        const list: CommunityMember[] = (json.results || []).filter((m: CommunityMember) => m.customer_id !== userId)
        if (!cancelled) setPeople(list)
      } catch (e: any) {
        if (!cancelled) setErrorPeople(e?.message || 'ERROR')
      } finally {
        if (!cancelled) setLoadingPeople(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [openNew, qd, sellerId, userId])

  useEffect(() => {
    if (openNew) return
    setQ('')
    setPeople([])
    setStartingId(null)
    setErrorPeople(null)
    setLoadingPeople(false)
  }, [openNew])

  async function startConversation(otherId: number) {
    setStartingId(otherId)
    try {
      const res = await fetch('/api/inbox/conversations/create', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-user-id': String(userId),
        },
        body: JSON.stringify({ otherId }),
      })
      const json = await res.json()
      if (!res.ok || !json?.ok) throw new Error(json?.error || 'CREATE_FAILED')

      const conversationId = Number(json.conversationId)

      // Asegura que exista en la lista: refetch (simple y robusto)
      await reloadConversations(conversationId)

      setOpenNew(false)
      selectConversation(conversationId)
    } catch (e: any) {
      setErrorPeople(e?.message || 'ERROR')
      setStartingId(null)
    }
  }

  const peerOnline =
    isOnline(
      selectedConv?.peer?.id
    )

  return (
    <div
      className={cn(
        'h-full min-h-0 w-full overflow-hidden bg-background',
        threadOnly
          ? 'relative'
          : isMobile
            ? 'relative'
            : 'grid grid-cols-[340px_1fr]'
      )}
    >
    <AnimatePresence initial={false} mode="wait">
    {!threadOnly && (!isMobile || !selectedId) && (
        <motion.div
        key="list"
        initial={{ x: 0, opacity: 1 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -40, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(isMobile ? "absolute inset-0" : "min-h-0")}
        >
      {/* LEFT: conversations */}
      <Card className="rounded-none border-0 border-r border-border/50 bg-muted/40 flex flex-col h-full min-h-0">
        <div className="p-4 border-b border-border/50 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-foreground truncate">Inbox</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <span className={cn('inline-flex h-2 w-2 rounded-full', connected ? 'bg-emerald-500' : 'bg-muted-foreground/50')} />
              {connected ? 'Online' : 'Offline'}
            </div>
          </div>

          <Button
            size="icon"
            variant="secondary"
            onClick={() => setOpenNew(true)}
            disabled={!sellerId}
            title={!sellerId ? 'Falta sellerId para buscar miembros' : 'Nuevo mensaje'}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2 space-y-1">
            {loadingConvs ? (
              <div className="text-sm text-muted-foreground p-3 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando…
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-sm text-muted-foreground p-3">
                Aún no hay conversaciones.
                <div className="mt-2">
                  <Button size="sm" variant="secondary" onClick={() => setOpenNew(true)} disabled={!sellerId}>
                    <Plus className="h-4 w-4 mr-2" /> Nuevo mensaje
                  </Button>
                </div>
              </div>
            ) : (
              conversations.map((c) => {
                const displayName =
                c.is_group
                    ? (c.title || `Grupo #${c.conversation_id}`)
                    : (c.peer?.name || c.title || `Conversación #${c.conversation_id}`)

                const avatarUrl =
                c.is_group ? null : (c.peer?.avatar_url || null)

                const initials = initialsFrom(displayName)

                const active = c.conversation_id === selectedId
                const peerIsOnline = isOnline(c.peer?.id)
                
                return (
                  <button
                    key={c.conversation_id}
                    onClick={() => selectConversation(c.conversation_id)}
                    className={cn(
                      'w-full flex items-start gap-3 p-3 text-left rounded-lg transition',
                      active ? 'bg-background/80 shadow-sm' : 'hover:bg-background/50'
                    )}
                  >
                    <div className="relative h-10 w-10 shrink-0">
                    <Avatar className="h-10 w-10">
                    {avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                        src={avatarUrl}
                        alt={displayName}
                        className="h-full w-full object-cover"
                        />
                    ) : null}

                    <AvatarFallback className="text-xs font-semibold">
                        {initials}
                    </AvatarFallback>
                    </Avatar>


                    {peerIsOnline ? (
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-background" />
                    ) : null}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm text-foreground truncate">
                          {displayName}
                        </div>
                        <div className="text-xs text-muted-foreground ml-2">
                          {formatMessageTime(c.last_message_at || (c as any).date_added)}
                        </div>
                      </div>

                      <div className="text-sm text-muted-foreground truncate">
                        {c.last_body || '—'}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </ScrollArea>
      </Card>
      </motion.div>
    )}
    </AnimatePresence>

<AnimatePresence initial={false} mode="wait">
  {(threadOnly || !isMobile || selectedId) && (
    <motion.div
      key="thread"
      initial={{ x: isMobile ? 360 : 0, opacity: isMobile ? 0.6 : 1 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 40, opacity: 0 }}
      transition={{ duration: 0.22 }}
      className={cn(
        threadOnly
          ? 'absolute inset-0'
          : isMobile
            ? 'absolute inset-0'
            : 'min-h-0',
        'h-full'
      )}
    >
      {/* RIGHT: chat */}
      <Card className="rounded-none border-0 flex h-full flex-col min-h-0">
        <div className="p-4 pr-16 border-b border-border/50 flex items-center justify-between gap-3">
          <div className="min-w-0">
            {/* Row 1 */}
            <div className="flex items-center gap-2 min-w-0">
              {isMobile && !threadOnly ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedId(null)}
                  className="-ml-2 h-8 px-2"
                >
                  ←
                </Button>
              ) : null}

              {/* (opcional) avatar del peer si ya lo tienes */}
              {selectedConv?.peer?.avatar_url ? (
                <img
                  src={selectedConv.peer.avatar_url}
                  alt={selectedConv.peer?.name || 'User'}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                  {initialsFrom(selectedConv?.peer?.name || selectedConv?.title || 'DM')}
                </div>
              )}

              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="font-semibold text-foreground truncate">
                    {selectedConv?.peer?.name ||
                      selectedConv?.title ||
                      (selectedId ? `Conversación #${selectedId}` : 'Selecciona una conversación')}
                  </div>

                  <span
                    className={cn(
                      'inline-flex h-2 w-2 rounded-full shrink-0',
                      peerOnline ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                    )}
                  />
                </div>

                {/* Row 2 */}
                <div className="text-xs text-muted-foreground truncate">
                  {selectedId ? (peerTyping ? 'Escribiendo…' : 'Mensajes directos') : 'Elige un chat del panel izquierdo'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() =>
                reloadConversations(selectedId ?? undefined)
              }
              disabled={loadingConvs}
              className="h-9 w-9 rounded-full"
              title="Refrescar"
              aria-label="Refrescar conversación"
            >
              {loadingConvs ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>

            {threadOnly && selectedId ? (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => {
                  router.push(`/inbox?c=${selectedId}`)
                }}
                className="h-9 w-9 rounded-full"
                title="Abrir Inbox completo"
                aria-label="Abrir Inbox completo"
              >
                <Inbox className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0" viewportRef={scrollViewportRef as any} onScrollCapture={onScrollCapture}>
          <div className="p-4 space-y-3">
            {loadingMsgs ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando mensajes…
              </div>
            ) : selectedId ? (
              messages.map((m) => {
                const mine = m.sender_id === userId
                const isLastMine = mine && m.message_id === lastMineId
                return (
                  <div
                    key={`${m.message_id}-${m.date_added}`}
                    className={cn('flex', mine ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[70%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                        mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      )}
                    >
                      <div className="whitespace-pre-wrap break-words">
                        <LinkifiedText text={m.body} />
                      </div>
                      <div className="mt-1 flex items-center justify-end gap-2 text-[10px] opacity-70">
                        <span>{formatMessageTime(m.date_added)}</span>
                        {isLastMine ? <span>{isLastMineSeen ? 'Visto' : 'Enviado'}</span> : null}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-sm text-muted-foreground">
                Selecciona una conversación o crea una nueva con el botón <strong>+</strong>.
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-border/50">
          <div className="flex gap-2">
            <Input
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder={selectedId ? 'Escribe un mensaje…' : 'Selecciona una conversación…'}
              disabled={!selectedId}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              className="flex-1"
            />
            <Button size="icon" onClick={send} disabled={!selectedId || !text.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>

          {!connected ? (
            <div className="mt-2 text-xs text-muted-foreground">
              Realtime está offline. Puedes seguir probando; cuando conecte, verás updates instantáneos.
            </div>
          ) : null}
        </div>
      </Card>
      </motion.div>
  )}
</AnimatePresence>

      {/* Modal Nuevo Mensaje */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="p-0 sm:max-w-xl overflow-hidden [&>button:first-child]:hidden">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="text-base">Nuevo mensaje</DialogTitle>

              <Button
                size="icon"
                variant="ghost"
                onClick={() => setOpenNew(false)}
                className="h-8 w-8"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, email o @screenname…"
                  className="pl-9"
                />
              </div>
            </div>

            {errorPeople ? (
              <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {String(errorPeople)}
              </div>
            ) : null}
          </DialogHeader>

          <div className="px-2 py-2">
            <ScrollArea className="h-[360px]">
              <div className="px-3 py-2">
                {!sellerId ? (
                  <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                    Falta <code>sellerId</code> para listar miembros.
                  </div>
                ) : loadingPeople ? (
                  <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Buscando miembros…
                  </div>
                ) : people.length === 0 ? (
                  <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                    {qd ? 'No encontré a nadie con ese criterio.' : 'Empieza a escribir para buscar miembros.'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {people.map((m) => {
                      const isStarting = startingId === m.customer_id
                      return (
                        <button
                          key={m.customer_id}
                          className={cn(
                            'w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition',
                            'hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/30'
                          )}
                          onClick={() => startConversation(m.customer_id)}
                          disabled={!!startingId}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 overflow-hidden rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                              {m.avatar_url ? (
                                <img src={m.avatar_url} alt={m.name} className="h-full w-full object-cover" />
                              ) : (
                                <span>{initialsFrom(m.name)}</span>
                              )}
                            </div>

                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium">{m.name}</div>
                              <div className="truncate text-xs text-muted-foreground">
                                {m.screenname ? `@${m.screenname}` : m.email || ''}
                              </div>
                            </div>
                          </div>

                          <div className="shrink-0">
                            {isStarting ? (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Abriendo…
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Enviar</span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="border-t px-5 py-3 text-xs text-muted-foreground hidden">
            Selecciona un miembro: si el chat ya existe, lo reutiliza automáticamente.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
